import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { RentalWithRelations, ExchangeRate } from "@/types/models";
import { format, differenceInMinutes } from "date-fns";
import { Clock, Bike, Calendar, DollarSign } from "lucide-react";

interface RentalCompletionModalProps {
  rental: RentalWithRelations;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  exchangeRate?: ExchangeRate;
}

const RentalCompletionModal = ({
  rental,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  exchangeRate
}: RentalCompletionModalProps) => {
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsConfirming(false);
    }
  }, [isOpen]);

  const startTime = rental.start_date ? format(new Date(rental.start_date), 'dd/MM/yy hh:mm a') : '';
  const expectedReturnTime = rental.expected_return_time ? format(new Date(rental.expected_return_time), 'h:mm a') : '';
  const actualReturnTime = format(new Date(), 'h:mm a');

  const calculateActualDuration = () => {
    if (rental.start_date) {
      const now = new Date();
      const start = new Date(rental.start_date);
      const minutes = differenceInMinutes(now, start);
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return '0h 0m';
  };

  const calculateOvertime = (): number => {
    if (rental.expected_return_time) {
      const now = new Date();
      const returnTime = new Date(rental.expected_return_time);     
      return differenceInMinutes(now, returnTime);
    }
    return 0;
  }

  const isOvertime = () => {
    const minutes = calculateOvertime();
    if (minutes) return minutes > 0;
    return false;
  };

  const handleConfirm = () => {
    setIsConfirming(true);
    onConfirm();
  };

  const totalAmountBs = rental.total_amount && exchangeRate?.usd_to_bs 
    ? (rental.total_amount * exchangeRate.usd_to_bs).toFixed(2) 
    : 'N/A';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bike className="h-5 w-5 text-bike-600" />
            Finalizar Servicio
          </DialogTitle>
          <DialogDescription>
            Confirma la entrega de la bicicleta y finalización del alquiler
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Bicicleta:</span>
              <span className="font-semibold">
                {rental.bike?.model} #{rental.bike?.number}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Cliente:</span>
              <span>{rental.customer?.name}</span>
            </div>

            <Separator />

            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Inicio:</span>
              <span className="text-sm font-medium">{startTime}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Retorno esperado:</span>
              <span className="text-sm font-medium">{expectedReturnTime}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Retorno real:</span>
              <span className="text-sm font-medium">{actualReturnTime} ({calculateActualDuration()})</span>
            </div>

            {isOvertime() && (
              <div className="bg-red-50 border border-red-200 p-2 rounded">
                <span className="text-red-700 text-sm font-medium">
                  ⚠️ Alquiler con tiempo excedido: { (calculateOvertime()).toFixed(0) } m 
                </span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Plan:</span>
              <Badge variant="outline" className="text-xs">
                {rental.plan?.name} - ${rental.plan?.price}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-600">Monto pagado: <span className="font-semibold">${rental.total_amount}</span></span>
              {exchangeRate && (
                <span className="text-sm font-semibold">(Bs. {totalAmountBs})</span>
              )}
            </div>
          </div>

          <div className="bg-bike-50 border border-bike-200 p-3 rounded-lg">
            <p className="text-sm text-bike-700 font-medium">
              ¿Confirmas que el cliente ha entregado la bicicleta en buenas condiciones?
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isLoading || isConfirming}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={isLoading || isConfirming}
            className="w-full bg-green-600 hover:bg-green-700 text-white"
          >
            {isConfirming ? "Finalizando..." : "Confirmar Entrega"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RentalCompletionModal;
